/**
 * ② Consentement.
 *
 * L'ecran juridiquement le plus sensible du produit.
 *
 *   - La case n'est JAMAIS pre-cochee : une case pre-cochee invalide le
 *     consentement, en RGPD comme en eIDAS. Non negociable. C'est pour
 *     cela que `checked` part de false et qu'aucun code ne le force.
 *   - Cocher ne vaut pas consentement : il faut une action explicite et
 *     distincte, d'ou le bouton separe.
 *   - Ce qui sera journalise est annonce. Le backend enregistre deja date,
 *     heure, adresse IP et agent utilisateur dans la piste d'audit ; le
 *     taire serait deloyal.
 */
import { useState } from "react";

import { ApiError, postConsent, type SignerView } from "../api";
import { CONSENT_TEXT_IS_PROVISIONAL, type t } from "../i18n";
import { ErrorPanel } from "../components/ErrorPanel";

interface Props {
  token: string;
  L: ReturnType<typeof t>;
  /** Texte servi par le serveur : celui qu'il enregistre dans la preuve. */
  consentement?: SignerView["consentement"];
  onDone: () => void;
  onDecline: () => void;
}

export function Consent({ token, L, consentement, onDone, onDecline }: Props) {
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [touched, setTouched] = useState(false);

  const submit = async () => {
    if (!checked) {
      setTouched(true);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await postConsent(token);
      onDone();
    } catch (e) {
      const err = e instanceof ApiError ? e : new ApiError("server", 0, null);
      // Consentement deja enregistre (par exemple un double clic, ou un
      // onglet reste ouvert) : ce n'est pas un echec, l'etape est
      // franchie. La bloquer ferait croire a une panne.
      if (err.kind === "conflict") {
        onDone();
        return;
      }
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="card stack">
      <div>
        <h1 className="card__title">{L.consentTitle}</h1>
      </div>

      {CONSENT_TEXT_IS_PROVISIONAL && !consentement && (
        <p className="provisional">⚠️ {L.provisional}</p>
      )}

      {/* Le texte du serveur d'abord : c'est celui qui sera inscrit dans le
          dossier de preuve. Repli sur la traduction locale seulement si un
          serveur anterieur ne le renvoie pas. */}
      {(consentement?.corps ?? L.consentBody) && <p>{consentement?.corps ?? L.consentBody}</p>}

      {/* Annonce de ce qui est journalise - exigence de loyaute, et les
          donnees sont bel et bien enregistrees. */}
      <p className="muted">{L.consentLogged}</p>

      <label className="consent">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => {
            setChecked(e.target.checked);
            setTouched(true);
          }}
          aria-describedby={touched && !checked ? "consent-error" : undefined}
        />
        <span>{consentement?.case ?? L.consentCheckbox}</span>
      </label>

      {touched && !checked && (
        <p id="consent-error" className="alert alert--error" role="alert">
          {L.consentRequired}
        </p>
      )}

      {error && <ErrorPanel error={error} L={L} />}

      {/* Plus de « revenir au document » depuis B-16 : le document est la,
          a cote, et le reste jusqu'a la signature. Un bouton qui promet de
          ramener a ce qu'on a deja sous les yeux ajoute un doute plutot
          qu'un recours. */}
      <div className="actions actions--split">
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => void submit()}
          disabled={!checked || submitting}
        >
          {L.consentSubmit}
        </button>
        <button type="button" className="btn btn--danger" onClick={onDecline}>
          {L.refuse}
        </button>
      </div>
    </section>
  );
}
